import { useMemo } from "react";

const CONFETTI_COLORS = ["var(--color-gold)", "var(--color-plum)", "var(--color-teal)"];
const SWAY_VARIANTS = ["confetti-sway-a", "confetti-sway-b", "confetti-sway-c"];
const FLUTTER_VARIANTS = ["confetti-flutter-a", "confetti-flutter-b"];

const PIECE_COUNT = 160;
/** 節目の記念日(→ ideas.md)は、量を増やして華やかにする */
const PIECE_COUNT_DENSE = 280;

interface Props {
  /** 節目の記念日の当日だけ true にして、量を増やす */
  dense?: boolean;
}

interface Piece {
  left: number;
  delay: number;
  duration: number;
  width: number;
  height: number;
  color: string;
  sway: string;
  flutter: string;
}

function pick<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)] as T;
}

function randomPieces(count: number): Piece[] {
  return Array.from({ length: count }, () => {
    const size = 5 + Math.random() * 5;
    return {
      left: Math.random() * 100,
      delay: Math.random() * 2.5,
      duration: 5.5 + Math.random() * 3,
      width: size,
      height: size * 0.45,
      color: pick(CONFETTI_COLORS),
      sway: pick(SWAY_VARIANTS),
      flutter: pick(FLUTTER_VARIANTS),
    };
  });
}

/**
 * 記念日当日に予定画面を開いた最初の数秒だけ画面全体に降らせる紙吹雪。
 * ライブラリは足さず(CLAUDE.md)、toast-in と同じ CSS アニメーションの流儀で自作する。
 *
 * 各片は 落下(fall)・横揺れ(sway)・傾きのパタパタ(flutter) を別々の入れ子要素に分けて
 * 独立に動かし、まっすぐ降るだけでなく舞うように見せている
 * (記念日カード自体の light-up は Schedule.tsx 側の .confetti-glow で別途行う)。
 */
export default function Confetti({ dense = false }: Props) {
  const pieces = useMemo(() => randomPieces(dense ? PIECE_COUNT_DENSE : PIECE_COUNT), [dense]);

  return (
    <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden" aria-hidden>
      {pieces.map((piece, index) => (
        <span
          key={index}
          className="confetti-piece absolute top-[-24px]"
          style={{
            left: `${piece.left}%`,
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
          }}
        >
          <span
            className={`confetti-sway ${piece.sway}`}
            style={{
              animationDelay: `${piece.delay}s`,
              animationDuration: `${piece.duration}s`,
            }}
          >
            <span
              className={`confetti-chip ${piece.flutter} rounded-[1px]`}
              style={{
                width: piece.width,
                height: piece.height,
                backgroundColor: piece.color,
                animationDelay: `${piece.delay}s`,
                animationDuration: `${piece.duration}s`,
              }}
            />
          </span>
        </span>
      ))}
    </div>
  );
}
